import React, { useState } from 'react';
import {
  Drawer,
  Form,
  Input,
  DatePicker,
  Switch,
  message,
  Space,
  Upload,
  Button,
  List,
  Popconfirm,
  Typography,
} from 'antd';
import {
  UploadOutlined,
  DeleteOutlined,
  FileOutlined,
} from '@ant-design/icons';
import type { UploadFile } from 'antd';
import { createFollowUp } from '../../../../services/followUp';
import { uploadAttachment } from '../../../../services/attachment';
import { Attachment } from '../../../../services/lead';
import dayjs from 'dayjs';
import { getErrorMessageFromUnknown, isFormValidationError } from '../../../../utils/errorHandler';

const { TextArea } = Input;
const { Text } = Typography;

interface AddFollowUpModalProps {
  visible: boolean;
  caseId: number;
  onCancel: () => void;
  onSuccess: () => void;
}

const AddFollowUpModal: React.FC<AddFollowUpModalProps> = ({
  visible,
  caseId,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  // Handle form submit
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // Prepare follow-up data
      const followUpData = {
        entity_type: 'case',
        entity_id: caseId,
        follow_type: 'user',  // 统一使用 user 类型
        content: values.content,
        next_follow_up_time: values.next_follow_up_time
          ? values.next_follow_up_time.format('YYYY-MM-DD HH:mm:ss')
          : undefined,
        reminder_enabled: values.reminder_enabled || false,
        attachments: attachments.map(att => ({
          id: att.id,
          file_name: att.file_name || '附件',
          file_path: att.file_path || '',
          file_size: att.file_size || 0,
        })),
      };

      await createFollowUp(followUpData);
      message.success('跟进记录已添加');
      handleClose();
      onSuccess();
    } catch (error: unknown) {
      console.error('Failed to create follow-up:', error);
      if (isFormValidationError(error)) {
        // Form validation error
        return;
      }
      message.error('添加跟进记录失败：' + getErrorMessageFromUnknown(error));
    } finally {
      setLoading(false);
    }
  };

  // Handle file upload
  const handleUpload = async (file: File): Promise<boolean> => {
    // Check file size (50MB limit)
    if (file.size > 50 * 1024 * 1024) {
      message.error('文件大小不能超过50MB');
      return false;
    }

    // Check max count
    if (attachments.length >= 5) {
      message.error('最多只能上传5个附件');
      return false;
    }

    setUploading(true);
    try {
      // Upload to temporary location (we'll associate with follow-up after creation)
      const attachment = await uploadAttachment(file, 'case', caseId);
      const newAttachments = [...attachments, attachment];
      setAttachments(newAttachments);

      // Add to file list for display
      const newFile: UploadFile = {
        uid: attachment.id.toString(),
        name: attachment.filename || attachment.file_name || '未知文件',
        status: 'done',
        url: attachment.file_path,
        size: attachment.file_size,
      };
      setFileList([...fileList, newFile]);

      message.success('上传成功');
    } catch (error: unknown) {
      console.error('Upload failed:', error);
      message.error('上传失败：' + getErrorMessageFromUnknown(error));
    } finally {
      setUploading(false);
    }

    return false; // Prevent default upload behavior
  };

  // Handle file delete
  const handleDeleteAttachment = (attachment: Attachment) => {
    const newAttachments = attachments.filter(a => a.id !== attachment.id);
    setAttachments(newAttachments);
    setFileList(fileList.filter(f => f.uid !== attachment.id.toString()));
    message.success('已移除附件');
  };

  // Format file size
  const formatFileSize = (size?: number): string => {
    if (!size) return '';
    if (size < 1024) return `${size}B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)}KB`;
    return `${(size / (1024 * 1024)).toFixed(1)}MB`;
  };

  // Handle modal close
  const handleClose = () => {
    form.resetFields();
    setAttachments([]);
    setFileList([]);
    onCancel();
  };

  return (
    <Drawer
      title="添加跟进记录"
      open={visible}
      onClose={handleClose}
      width={600}
      destroyOnClose
      footer={
        <Space style={{ float: 'right' }}>
          <Button onClick={handleClose}>取消</Button>
          <Button type="primary" onClick={handleSubmit} loading={loading}>
            提交
          </Button>
        </Space>
      }
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          follow_time: dayjs(),
          reminder_enabled: false,
        }}
      >
        <Form.Item
          name="follow_time"
          label="跟进时间"
          rules={[{ required: true, message: '请选择跟进时间' }]}
        >
          <DatePicker
            showTime
            format="YYYY-MM-DD HH:mm"
            style={{ width: '100%' }}
            placeholder="选择跟进时间"
          />
        </Form.Item>

        <Form.Item
          name="content"
          label="跟进内容"
          rules={[
            { required: true, message: '请输入跟进内容' },
            { min: 5, message: '跟进内容至少5个字符' },
          ]}
        >
          <TextArea
            rows={6}
            placeholder="请详细描述本次跟进的内容..."
            showCount
            maxLength={2000}
          />
        </Form.Item>

        {/* File Upload */}
        <Form.Item label="附件">
          <Space direction="vertical" style={{ width: '100%' }}>
            <Upload
              beforeUpload={handleUpload}
              showUploadList={false}
              multiple
              disabled={uploading || attachments.length >= 5}
            >
              <Button
                icon={<UploadOutlined />}
                loading={uploading}
                disabled={attachments.length >= 5}
              >
                {uploading ? '上传中...' : '上传附件'}
              </Button>
            </Upload>

            {attachments.length > 0 && (
              <List
                size="small"
                dataSource={attachments}
                renderItem={(attachment) => (
                  <List.Item
                    actions={[
                      <Popconfirm
                        key="delete"
                        title="确定要删除这个附件吗？"
                        onConfirm={() => handleDeleteAttachment(attachment)}
                        okText="确定"
                        cancelText="取消"
                      >
                        <Button
                          type="text"
                          size="small"
                          icon={<DeleteOutlined />}
                          danger
                        >
                          删除
                        </Button>
                      </Popconfirm>,
                    ]}
                  >
                    <List.Item.Meta
                      avatar={<FileOutlined />}
                      title={
                        <Space>
                          <Text>{attachment.file_name}</Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {formatFileSize(attachment.file_size)}
                          </Text>
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            )}

            <Text type="secondary" style={{ fontSize: 12 }}>
              支持上传最多5个附件，单个文件最大50MB
            </Text>
          </Space>
        </Form.Item>

        <Form.Item
          name="next_follow_up_time"
          label="下次跟进时间"
          tooltip="可选，设置下次跟进的计划时间"
        >
          <DatePicker
            showTime
            format="YYYY-MM-DD HH:mm"
            style={{ width: '100%' }}
            placeholder="选择下次跟进时间（可选）"
            disabledDate={(current) => current && current < dayjs().startOf('day')}
          />
        </Form.Item>

        <Form.Item
          name="reminder_enabled"
          label="提醒设置"
          valuePropName="checked"
          tooltip="开启后将在下次跟进时间到达时发送提醒"
        >
          <Switch checkedChildren="开启" unCheckedChildren="关闭" />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default AddFollowUpModal;
